import "./UserInformation.css";
import {
  useMemo,
  useState,
  useRef,
  useContext,
  useEffect
} from "react";

import { AlarmContext } from "../context/AlarmContext";
import { useCompetencyEngine } from "../competency/CompetencyEngine";
import { getLoginTime } from "../utils/sessionTime";

function UserInformation({ open, onClose }) {

  // ================= ALARM CONTEXT =================
  const { activeAlarms } = useContext(AlarmContext);

  // ================= DRAG STATE =================
  const [pos, setPos] = useState({ x: 300, y: 100 });

  const dragging = useRef(false);
  const offset = useRef({ x: 0, y: 0 });

  // ================= GLOBAL DRAG ENGINE =================
  useEffect(() => {

    const handleMove = (e) => {
      if (!dragging.current) return;

      setPos({
        x: e.clientX - offset.current.x,
        y: e.clientY - offset.current.y
      });
    };

    const handleUp = () => {
      dragging.current = false;
    };

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);

    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };

  }, []);

  const startDrag = (e) => {
    dragging.current = true;

    offset.current = {
      x: e.clientX - pos.x,
      y: e.clientY - pos.y
    };
  };

  // ================= USER DATA =================
  const userName =
    localStorage.getItem("userName") ||
    localStorage.getItem("loginName") ||
    "NK Tech User";

  const accessLevel =
    localStorage.getItem("userAccessLevel") ||
    "Visitor Access";



const loginISO = getLoginTime();

const loginDateObj = useMemo(() => {
  return loginISO ? new Date(loginISO) : new Date();
}, [loginISO]);

 // ================= LIVE CLOCK =================
const [now, setNow] = useState(new Date());

useEffect(() => {

  const timer = setInterval(() => {
    setNow(new Date());
  }, 1000);

  return () => clearInterval(timer);

}, []);

// ================= TIME VALUES =================
const loginDate =
  loginDateObj.toLocaleDateString();

const loginTime =
  loginDateObj.toLocaleTimeString();

const currentTime =
  now.toLocaleTimeString();

// ================= SESSION DURATION =================
const sessionDuration = useMemo(() => {

  const diffMs = now - loginDateObj;

  const totalMinutes =
    Math.floor(diffMs / 60000);

  const days =
    Math.floor(totalMinutes / 1440);

  const hours =
    Math.floor((totalMinutes % 1440) / 60);

  const minutes =
    totalMinutes % 60;

  return `
${days} Day${days !== 1 ? "s" : ""}
${hours} Hour${hours !== 1 ? "s" : ""}
${minutes} Minute${minutes !== 1 ? "s" : ""}
  `.trim();

}, [now, loginDateObj]);

  // ================= COMPETENCY ENGINE (FIXED - NO UI CHANGES) =================
  const { statement, status, score } = useCompetencyEngine(activeAlarms || []);

  // ================= ACCESS INFO =================
  const accessDescriptions = {
    "Visitor Access":
      "Read-only access for platform familiarization and basic system navigation.",

    "Operator Console Access":
      "Access to operational process monitoring, alarm handling, and control interaction within the simulator environment.",

    "Process Engineering Suite":
      "Advanced access for process analysis, tuning studies, and optimization.",

    "Plant Engineering Access":
      "Full engineering-level access for plant-wide operational analysis."
  };

  const accessColor = {
    "Visitor Access": "#95a5a6",
    "Operator Console Access": "#00ffff",
    "Process Engineering Suite": "#ff9800",
    "Plant Engineering Access": "#ff4040"
  };

  // ================= SHARE =================
  // ================= SHARE =================

const handleShare = async () => {

  try {

    const report = `

NK TECH INDUSTRIAL DCS AND DIGITAL TWIN SOLUTION
USER INFORMATION REPORT

----------------------------------------

NAME:
${userName}

----------------------------------------

ACCESS LEVEL:
${accessLevel}

${accessDescriptions[accessLevel]}

----------------------------------------

LOGIN DATE:
${loginDate}

LOGIN TIME:
${loginTime}

CURRENT TIME:
${currentTime}

SESSION DURATION:
${sessionDuration}

SESSION STATUS:
ACTIVE

----------------------------------------

MAIN SESSION INFORMATION

${userName} is currently using NK Tech
Industrial DCS Simulator under
${accessLevel}.

The simulator is designed to emulate key
operational behaviors of a Distributed
Control System (DCS).

During this session, the user has been
active for ${sessionDuration}.

----------------------------------------

OPERATIONAL COMPETENCY ASSESSMENT

${statement}

----------------------------------------

COMPETENCY STATUS

${status} (${score}%)

----------------------------------------

DISCLAIMER

The use of this simulator does not
constitute formal operational certification
or confirmation of field readiness for
live plant operations.

Competency levels may vary depending on
individual technical understanding,
operational experience, process knowledge,
response to abnormal conditions, and
supervised field exposure.

Organizations are advised to conduct
independent technical evaluations,
supervised assessments, operational
mentorship programs, and competency
verification procedures before assigning
personnel to real-time operational
responsibilities.

----------------------------------------

Generated From:
NK TECH INDUSTRIAL DCS AND DIGITAL TWIN SOLUTION

`;

    // ================= NATIVE SHARE =================

    if (navigator.share) {

      await navigator.share({

        title:
          "NK Tech User Information",

        text: report

      });

    } else {

      // ================= FALLBACK =================

      await navigator.clipboard.writeText(report);

      alert(
        "User Information copied to clipboard."
      );
    }

  } catch (err) {

    console.log("Share Error:", err);

  }

};

  if (!open) return null;

  return (
    <div
      className="user-info-overlay"
      onMouseMove={() => {}}
    >

      <div
        className="user-info-window user-drag-layer"
        style={{
          position: "absolute",
          left: pos.x,
          top: pos.y
        }}
      >

        {/* HEADER */}
        <div className="user-info-header" onMouseDown={startDrag}>
          <div className="user-info-title-wrapper">

            <div className="user-info-main-title">
              NK TECH INDUSTRIAL DCS AND DIGITAL TWIN SOLUTION
            </div>

            <div className="user-info-sub-title">
              USER INFORMATION
            </div>

          </div>

          <button
            className="user-info-close"
            onClick={onClose}
          >
            ✕
          </button>

        </div>

        {/* BODY (UNCHANGED CONTENT) */}
        <div className="user-info-body user-info-scroll">

          {/* NAME */}
          <div className="user-info-row">
            <span className="label">Name:</span>
            <span className="line-value">{userName}</span>
          </div>

          {/* ACCESS LEVEL */}
          <div className="user-info-row">
            <span className="label">Access Level:</span>
            <span
              className="line-value access-badge"
              style={{ color: accessColor[accessLevel] }}
            >
              {accessLevel}
            </span>
          </div>

          <div className="access-description">
            {accessDescriptions[accessLevel]}
          </div>

          {/* LOGIN DATE */}
          <div className="user-info-row">
            <span className="label">Login Date:</span>
            <span className="line-value">{loginDate}</span>
          </div>

          {/* LOGIN TIME */}
          <div className="user-info-row">
            <span className="label">Login Time:</span>
            <span className="line-value">{loginTime}</span>
          </div>

          {/* CURRENT TIME */}
          <div className="user-info-row">
            <span className="label">Current Time:</span>
            <span className="line-value">{currentTime}</span>
          </div>

          {/* SESSION DURATION */}
          <div className="user-info-row">
            <span className="label">Session Duration:</span>
            <span className="line-value">{sessionDuration}</span>
          </div>

          {/* STATUS */}
          <div className="user-info-row">
            <span className="label">Session Status:</span>
            <span className="line-value active-status">
              ACTIVE
            </span>
          </div>

          {/* MAIN STATEMENT (UNCHANGED) */}
          <div className="user-info-paragraph">

            <strong>{userName}</strong> is currently using NK Tech
            Industrial DCS Simulator under <strong>{accessLevel}</strong>.

            The simulator is designed to emulate key operational behaviors
            of a Distributed Control System (DCS).

            During this session, the user has been active for
            <strong> {sessionDuration}</strong>.

          </div>

          {/* COMPETENCY (ONLY ADDITION, NO REMOVAL) */}
          <div className="user-info-paragraph">
            <strong>Operational Competency Assessment:</strong>
            <br /><br />

            <pre style={{
              whiteSpace: "pre-wrap",
              fontFamily: "inherit"
            }}>
              {statement}
            </pre>
          </div>

          <div className="user-info-row">
            <span className="label">Competency Status:</span>
            <span className="line-value">
              {status} ({score}%)
            </span>
          </div>

          {/* DISCLAIMER (UNCHANGED) */}
          <div className="user-info-disclaimer">

            <strong>Disclaimer:</strong>

            The use of this simulator does not constitute formal
            operational certification or confirmation of field
            readiness for live plant operations.

            Competency levels may vary depending on individual
            technical understanding, operational experience,
            process knowledge, response to abnormal conditions,
            and supervised field exposure.

            Organizations are advised to conduct independent
            technical evaluations, supervised assessments,
            operational mentorship programs, and competency
            verification procedures before assigning personnel
            to real-time operational responsibilities.

          </div>

        </div>

        {/* FOOTER (UNCHANGED) */}
        <div className="user-info-footer">

          <button onClick={() => window.print()}>
            🖨 Print
          </button>

          <button onClick={handleShare}>
            📤 Share
          </button>

        </div>

      </div>
    </div>
  );
}

export default UserInformation;